import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowRight, Activity, Terminal as TerminalIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const formatNum = (n) => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US');
};

const HeroSection = ({ data, nodesData }) => {
  const { t, language } = useLanguage();

  const translations = {
    tr: {
      badge: 'SEC-OPS · CANLI TEHDİT İSTİHBARATI',
      users: 'Kullanıcı',
      bgpSessions: 'BGP Oturumu',
      webhooks: 'Webhook',
      countries: 'Ülke',
      ipList: 'IP Listesi',
      setup: 'Kurulum Rehberi',
      kpiTitle: 'Canlı Metrikler',
      kpiSub: 'Son 60 saniye',
    },
    en: {
      badge: 'SEC-OPS · REAL-TIME THREAT INTELLIGENCE',
      users: 'Users',
      bgpSessions: 'BGP Sessions',
      webhooks: 'Webhooks',
      countries: 'Countries',
      ipList: 'IP List',
      setup: 'Setup Guide',
      kpiTitle: 'Live Metrics',
      kpiSub: 'Last 60 seconds',
    },
  };

  const txt = translations[language] || translations.en;

  const stats = [
    { value: data?.total_users, label: txt.users, tone: 'text-primary' },
    { value: data?.total_bgp_sessions, label: txt.bgpSessions, tone: 'text-cyan-400' },
    { value: data?.total_webhooks, label: txt.webhooks, tone: 'text-violet-400' },
    { value: nodesData?.uniqueCountryCount, label: txt.countries, tone: 'text-amber-400' },
  ];

  // Remaining IPs in the feed after the 5 shown in the terminal
  const moreEntries = typeof data?.current_ips === 'number'
    ? Math.max(data.current_ips - 5, 0)
    : null;

  return (
    <section
      className="relative overflow-hidden border-b border-border"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 grid-bg mask-radial-fade opacity-70 pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute top-6 left-6 w-4 h-4 border-l-2 border-t-2 border-primary/60 hidden md:block" />
      <div className="absolute top-6 right-6 w-4 h-4 border-r-2 border-t-2 border-primary/60 hidden md:block" />
      <div className="absolute bottom-6 left-6 w-4 h-4 border-l-2 border-b-2 border-primary/60 hidden md:block" />
      <div className="absolute bottom-6 right-6 w-4 h-4 border-r-2 border-b-2 border-primary/60 hidden md:block" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <div className="soc-badge mb-6">
              <span className="status-dot" />
              <span>{txt.badge}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-5 text-foreground">
              Threat<span className="text-primary">.</span>live
            </h1>

            <div className="border-l-2 border-primary pl-4 mb-8 max-w-xl">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('aboutDescription')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-10">
              <Button
                asChild
                size="lg"
                className="h-11 px-6 sharp font-mono text-xs uppercase tracking-[0.14em] bg-primary text-primary-foreground hover:bg-primary/90 border border-primary shadow-premium"
              >
                <a href="https://threat.live/ip-list" target="_blank" rel="noopener noreferrer">
                  {txt.ipList}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-11 px-6 sharp font-mono text-xs uppercase tracking-[0.14em] border-primary dark:border-emerald-500/50 hover:bg-muted hover:border-primary"
              >
                <a href="https://threat.live/setup" target="_blank" rel="noopener noreferrer">
                  {txt.setup}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="status-dot" />
                <span>FEED OPERATIONAL</span>
              </span>
              <span className="text-border">·</span>
              <span>TLS 1.3 · E2E</span>
              <span className="text-border">·</span>
              <span>THREAT FEED READY</span>
              <span className="text-border hidden sm:inline">·</span>
              <span className="hidden sm:inline">
                SHA-256 <span className="text-foreground">a1f3…9c4d</span>
              </span>
            </div>
          </div>

          {/* RIGHT — terminal + KPI block */}
          <div className="lg:col-span-5">
            <div className="terminal mb-4 scanlines">
              <div className="terminal__header">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 sharp bg-rose-500/80" />
                  <span className="inline-block w-2.5 h-2.5 sharp bg-amber-500/80" />
                  <span className="inline-block w-2.5 h-2.5 sharp bg-emerald-500/80" />
                </span>
                <TerminalIcon className="h-3 w-3 opacity-70" />
                <span className="ml-1">analyst@threat.live : ~</span>
                <span className="ml-auto soc-badge soc-badge--neutral !py-0 !text-[9px]">
                  SSH · 2048-BIT
                </span>
              </div>
              <div className="terminal__body">
                <div>
                  <span className="terminal__prompt">$</span>{' '}
                  <span>curl -s https://list.threat.live/ | head -n 5</span>
                </div>
                <div className="terminal__comment"># fetching community threat feed…</div>
                <div className="terminal__value">172.16.3.5</div>
                <div className="terminal__value">10.84.221.17</div>
                <div className="terminal__value">192.168.44.129</div>
                <div className="terminal__value">172.31.198.204</div>
                <div className="terminal__value">10.0.57.88</div>
                <div className="terminal__comment">
                  # <span className="tabular text-white dark:text-foreground/80">{formatNum(moreEntries)}</span> more entries · feed:{' '}
                  <span className="text-primary">OPERATIONAL</span>
                </div>
                <div className="caret">
                  <span className="terminal__prompt">$</span>{' '}
                </div>
              </div>
            </div>

            {/* KPI grid */}
            <div className="border border-border bg-card corner-ticks">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/40">
                <div className="flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">
                    {txt.kpiTitle}
                  </span>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  {txt.kpiSub}
                </span>
              </div>
              <div className="grid grid-cols-2">
                {stats.map((s, i) => (
                  <div
                    key={i}
                    className={`p-4 border-border ${i % 2 === 0 ? 'border-r' : ''} ${i < 2 ? 'border-b' : ''}`}
                  >
                    <div className={`font-mono tabular text-2xl md:text-3xl font-bold ${s.tone}`}>
                      {s.value !== undefined && s.value !== null ? s.value : '—'}
                    </div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
